import { isSupplierAvailableFromGrants } from "../../shared/rbac/crossResourceAvailability";
import type { GrantRow } from "../../shared/rbac/grantQueries";

type SpaceWithGrants = {
  id: string;
  roleGrantsAsScope?: readonly GrantRow[];
};

type AppWithGrants = {
  id: string;
  name: string;
  roleGrantsAsScope?: readonly GrantRow[];
};

export type MoveTargetAccess = {
  accessibleSpaceIds: Set<string>;
  missingAppsBySpace: Map<string, string[]>;
};

export function checkMoveTargetAccess({
  spaces,
  workflowAppIds,
  allApps,
  userGroupMemberships,
}: {
  spaces: readonly SpaceWithGrants[];
  workflowAppIds: ReadonlySet<string>;
  allApps: readonly AppWithGrants[];
  userGroupMemberships: ReadonlyMap<string, readonly string[]>;
}): MoveTargetAccess {
  if (workflowAppIds.size === 0) {
    return {
      accessibleSpaceIds: new Set(spaces.map((s) => s.id)),
      missingAppsBySpace: new Map(),
    };
  }

  const appById = new Map<string, AppWithGrants>();
  for (const app of allApps) {
    if (workflowAppIds.has(app.id)) appById.set(app.id, app);
  }

  const accessibleSpaceIds = new Set<string>();
  const missingAppsBySpace = new Map<string, string[]>();

  for (const space of spaces) {
    const spaceGrants = space.roleGrantsAsScope ?? [];
    if (spaceGrants.length === 0) {
      const allNames = [...appById.values()].map((a) => a.name);
      if (allNames.length > 0) missingAppsBySpace.set(space.id, allNames);
      continue;
    }

    const missing: string[] = [];
    for (const appId of workflowAppIds) {
      const app = appById.get(appId);
      if (
        !isSupplierAvailableFromGrants(
          "space_uses_connected_apps",
          spaceGrants,
          app?.roleGrantsAsScope ?? [],
          userGroupMemberships
        )
      ) {
        missing.push(app?.name ?? appId);
      }
    }

    if (missing.length === 0) {
      accessibleSpaceIds.add(space.id);
    } else {
      missingAppsBySpace.set(space.id, missing);
    }
  }

  return { accessibleSpaceIds, missingAppsBySpace };
}
